// ScenarioAnalysis.jsx
import {
    useState,
    useEffect,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import html2canvas from 'html2canvas'
import axios from 'axios'
import Swal from 'sweetalert2'

import SingleRowWrapper from '../../components/Dashboard/Layouts/Row'
import MultilineChartCard from '../../components/MultilineChartCard'
import EmissionVsReduction from '../../components/Dashboard/EmissionVsReduction'
import { setFabMessage } from '../../features/layout/helpFabSlice'

const onDownloadImage = async (element, label) => {
    const canvas = await html2canvas(document.getElementById(element))
    const image = canvas.toDataURL('image/png', 1.0)

    const fakeLink = window.document.createElement('a')
    fakeLink.style = 'display:none'
    fakeLink.download = label
    fakeLink.href = image

    document.body.appendChild(fakeLink)
    fakeLink.click()
    document.body.removeChild(fakeLink)

    fakeLink.remove()
}

// ✅ helpers
const clampDigits = (v) => String(v ?? '').replace(/[^\d]/g, '').slice(0, 4)

const toYearOrNull = (v) => {
    const s = String(v ?? '').trim()
    if (!/^\d{4}$/.test(s)) return null
    const n = Number(s)
    if (n < 1900 || n > 3000) return null // adjust if needed
    return n
}

function ScenarioAnalysis() {
    const account = useSelector((state) => state.account.value)
    const { t } = useTranslation()
    const dispatch = useDispatch()

    // ✅ store as year numbers (not Date objects)
    const [startYear, setStartYear] = useState(null) // number | null
    const [endYear, setEndYear] = useState(null)     // number | null

    // ✅ raw input text for nicer typing UX
    const [startYearText, setStartYearText] = useState('')
    const [endYearText, setEndYearText] = useState('')
    const [touched, setTouched] = useState(false)

    const isRangeValid =
        startYear != null &&
        endYear != null &&
        startYear < endYear

    const onStartYearChange = (e) => {
        const raw = clampDigits(e.target.value)
        setStartYearText(raw)
        setStartYear(toYearOrNull(raw))
    }

    const onEndYearChange = (e) => {
        const raw = clampDigits(e.target.value)
        setEndYearText(raw)
        setEndYear(toYearOrNull(raw))
    }

    const onBlur = () => setTouched(true)

    const hintText = (() => {
        if (!touched) return t('scenario.yearRange.hintExample')
        if (!startYearText && !endYearText) return t('scenario.yearRange.hintFill')
        if (startYear == null || endYear == null) return t('scenario.yearRange.hint4Digit')
        if (startYear >= endYear) return t('scenario.yearRange.hintOrder')
        return ''
    })()

    const hintIsError =
        touched &&
        (startYearText || endYearText) &&
        (!isRangeValid)

    useEffect(() => {
        if (account['client_subscription'] === 1) {
            dispatch(setFabMessage({
                title: 'setting.emission.custom.title1',
                message: 'setting.emission.custom.hook1',
                whatsappText: 'Hello TruCarbon, can you help me plan a Decarbonization Roadmap?',
            }))
        }

        const controller = new AbortController()

        const getYearRange = async () => {
            try {
                const response = await axios({
                    withCredentials: true,
                    method: 'GET',
                    headers: { 'content-type': 'application/json' },
                    url: `${process.env.REACT_APP_BASE_SERVER}/scenario-analysis-initial-year-range-lookup`,
                    signal: controller.signal,
                })

                if (response.data.ok) {
                    const sy = Number(response.data.data.startYear)
                    const ey = 2060

                    setStartYear(sy)
                    setEndYear(ey)
                    setStartYearText(String(sy))
                    setEndYearText(String(ey))
                }
            } catch (e) {
                if (
                    !e.response?.data?.ok &&
                    e.response?.data?.notFound
                ) {
                    Swal.fire({
                        title: '',
                        text: e.response.data.message,
                        icon: 'error',
                        confirmButtonColor: '#4c638c',
                    })
                }
            }
        }

        getYearRange()
        return () => controller.abort()
    }, [])

    return (
        <div className='page-content'>
            <div className='container-fluid'>

                <div className='row'>
                    <div className='col-12'>
                        <div className='align-items-center d-flex justify-content-between mb-4'>
                            <h4 className='mb-sm-0' style={{ color: '#09564B' }}>
                                {t('scenario.scenarioAnalysisAbsoluteBasedEmissions')}
                            </h4>

                            <div style={{ minWidth: '18rem' }}>
                                <div
                                    className="d-flex align-items-center"
                                    style={{
                                        border: hintIsError ? '1px solid #dc3545' : '1px solid rgba(9, 86, 75, 0.25)',
                                        borderRadius: '10px',
                                        padding: '0.35rem 0.5rem',
                                        background: '#fff',
                                        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
                                        gap: '0.5rem',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            color: '#09564B',
                                            padding: '0.15rem 0.45rem',
                                            borderRadius: '999px',
                                            background: 'rgba(9, 86, 75, 0.08)',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {t('scenario.yearRange.label')}
                                    </div>

                                    <input
                                        value={startYearText}
                                        onChange={onStartYearChange}
                                        onBlur={onBlur}
                                        placeholder={t('scenario.yearRange.startPlaceholder')}
                                        inputMode="numeric"
                                        autoComplete="off"
                                        className="form-control"
                                        style={{
                                            height: '32px',
                                            padding: '0.2rem 0.5rem',
                                            fontSize: '13px',
                                            border: 'none',
                                            background: 'transparent',
                                            boxShadow: 'none',
                                            textAlign: 'center',
                                            width: '4.6rem',
                                        }}
                                    />

                                    <div
                                        style={{
                                            color: 'rgba(9, 86, 75, 0.55)',
                                            fontSize: '14px',
                                            fontWeight: 700,
                                            userSelect: 'none',
                                        }}
                                        title={t('scenario.yearRange.to')}
                                    >
                                        →
                                    </div>

                                    <input
                                        value={endYearText}
                                        onChange={onEndYearChange}
                                        onBlur={onBlur}
                                        placeholder={t('scenario.yearRange.endPlaceholder')}
                                        inputMode="numeric"
                                        autoComplete="off"
                                        className="form-control"
                                        style={{
                                            height: '32px',
                                            padding: '0.2rem 0.5rem',
                                            fontSize: '13px',
                                            border: 'none',
                                            background: 'transparent',
                                            boxShadow: 'none',
                                            textAlign: 'center',
                                            width: '4.6rem',
                                        }}
                                    />

                                    {(startYearText || endYearText) && (
                                        <button
                                            type="button"
                                            className="btn btn-sm"
                                            onClick={() => {
                                                setStartYear(null)
                                                setEndYear(null)
                                                setStartYearText('')
                                                setEndYearText('')
                                                setTouched(false)
                                            }}
                                            style={{
                                                border: '1px solid rgba(0,0,0,0.08)',
                                                background: '#fff',
                                                borderRadius: '8px',
                                                padding: '0.15rem 0.4rem',
                                                lineHeight: 1,
                                                fontSize: '12px',
                                            }}
                                            title={t('scenario.yearRange.clear')}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>

                                <div
                                    style={{
                                        marginTop: '0.25rem',
                                        fontSize: '11px',
                                        color: hintIsError ? '#dc3545' : 'rgba(9, 86, 75, 0.65)',
                                        textAlign: 'right',
                                    }}
                                >
                                    {hintText}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {account['client_subscription'] === 1 && (
                    <SingleRowWrapper>
                        <MultilineChartCard
                            chartId='emissionVsTarget'
                            onDownloadImage={onDownloadImage}
                            startYear={isRangeValid ? startYear : null}
                            endYear={isRangeValid ? endYear : null}
                            isDummyMode={true}
                        />
                    </SingleRowWrapper>
                )}

                {account['client_subscription'] > 1 && (
                    <>
                        <SingleRowWrapper>
                            <MultilineChartCard
                                chartId='emissionVsTarget'
                                onDownloadImage={onDownloadImage}
                                startYear={isRangeValid ? startYear : null}
                                endYear={isRangeValid ? endYear : null}
                            />
                        </SingleRowWrapper>

                        <SingleRowWrapper>
                            <EmissionVsReduction
                                chartId='emissionVsReduction'
                                filterLabel={t('scenario.chart.stackedbar.filterByType')}
                                onDownloadImage={onDownloadImage}
                                startYear={isRangeValid ? startYear : null}
                                endYear={isRangeValid ? endYear : null}
                            />
                        </SingleRowWrapper>
                    </>
                )}

            </div>
        </div>
    )
}

export default ScenarioAnalysis
